/* eslint-disable max-lines */
import { useMutation, useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { LogOutIcon, Trash2Icon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useAuthenticatedUser } from '@/components/providers/AuthenticatedUserProvider';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import {
  deleteProject,
  leaveProject,
  type Project,
  projectQueries,
} from '@/lib/queries/project';
import { generateMutationKey } from '@/lib/queries/utils';

interface Props {
  mainProject: Project;
}

export const DangerZone = ({ mainProject }: Props): JSX.Element => {
  const { toast } = useToast();
  const { user } = useAuthenticatedUser();
  const router = useRouter();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);

  const { data: projectDetails } = useQuery(
    projectQueries.detail(mainProject.id),
  );

  const isAdmin = useMemo(() => {
    return projectDetails?.members.some(
      member => member.role === 'admin' && member.email === user.email,
    );
  }, [projectDetails, user.email]);

  const isOnlyAdmin = useMemo(() => {
    return isAdmin === undefined
      ? undefined
      : isAdmin &&
          projectDetails?.members.filter(member => member.role === 'admin')
            .length === 1;
  }, [isAdmin, projectDetails]);

  const { isPending: isDeleting, mutateAsync: doDeleteProject } = useMutation({
    mutationKey: generateMutationKey(projectQueries.default(), 'delete', true),
    mutationFn: async () => deleteProject(mainProject.id),
    onError: () => {
      toast({
        title: 'configuration.dangerZone.deleteProject.dialog.button.error',
        variant: 'destructive',
      });
    },
    onSuccess: () => {
      toast({
        title: 'configuration.dangerZone.deleteProject.dialog.button.success',
        variant: 'default',
      });
      router.navigate({
        to: '/new-project',
      });
    },
  });

  const { isPending: isLeaving, mutateAsync: doLeaveProject } = useMutation({
    mutationKey: generateMutationKey(projectQueries.default(), 'leave', true),
    mutationFn: async () => leaveProject(mainProject.id),
    onError: () => {
      toast({
        title: 'configuration.dangerZone.leaveProject.dialog.button.error',
        variant: 'destructive',
      });
    },
    onSuccess: () => {
      toast({
        title: 'configuration.dangerZone.leaveProject.dialog.button.success',
        variant: 'default',
      });
      router.navigate({
        to: '/new-project',
      });
    },
  });

  return (
    <section>
      <div className="mt-1">
        <div>
          <h1 className="text-xl mb-2">
            <FormattedMessage id="configuration.dangerZone.title" />
          </h1>
        </div>
        <div className="flex flex-row">
          <TooltipProvider disableHoverableContent>
            <Tooltip delayDuration={100}>
              <TooltipTrigger asChild>
                <div>
                  <Button
                    variant="destructive"
                    className="text-white mr-4"
                    onClick={() => setLeaveDialogOpen(true)}
                    disabled={isLeaving || isOnlyAdmin !== false}
                  >
                    <LogOutIcon size={24} className="inline mr-2" />
                    <FormattedMessage id="configuration.dangerZone.leaveProject.button" />
                  </Button>
                </div>
              </TooltipTrigger>
              {isOnlyAdmin === true && (
                <TooltipContent side="right">
                  <FormattedMessage id="configuration.dangerZone.leaveProject.lastAdminTooltip" />
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
          {isAdmin === true && (
            <Button
              variant="destructive"
              className="text-white"
              onClick={() => setDeleteDialogOpen(true)}
              disabled={isDeleting}
            >
              <Trash2Icon size={24} className="inline mr-2" />
              <FormattedMessage id="configuration.dangerZone.deleteProject.button" />
            </Button>
          )}
        </div>
      </div>
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              <FormattedMessage id="configuration.dangerZone.deleteProject.dialog.title" />
            </DialogTitle>
            <DialogDescription>
              <FormattedMessage id="configuration.dangerZone.deleteProject.dialog.subtitle" />
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={isDeleting}
              onClick={() => setDeleteDialogOpen(false)}
            >
              <FormattedMessage id="configuration.dangerZone.deleteProject.dialog.cancel" />
            </Button>
            <Button
              variant="destructive"
              disabled={isDeleting}
              onClick={() => doDeleteProject()}
            >
              {isDeleting ? (
                <FormattedMessage id="configuration.dangerZone.deleteProject.dialog.button.pending" />
              ) : (
                <FormattedMessage id="configuration.dangerZone.deleteProject.dialog.button.label" />
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={leaveDialogOpen} onOpenChange={setLeaveDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              <FormattedMessage id="configuration.dangerZone.leaveProject.dialog.title" />
            </DialogTitle>
            <DialogDescription>
              <FormattedMessage id="configuration.dangerZone.leaveProject.dialog.subtitle" />
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={isLeaving}
              onClick={() => setLeaveDialogOpen(false)}
            >
              <FormattedMessage id="configuration.dangerZone.leaveProject.dialog.cancel" />
            </Button>
            <Button
              variant="destructive"
              disabled={isLeaving}
              onClick={() => doLeaveProject()}
            >
              {isLeaving ? (
                <FormattedMessage id="configuration.dangerZone.leaveProject.dialog.button.pending" />
              ) : (
                <FormattedMessage id="configuration.dangerZone.leaveProject.dialog.button.label" />
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};
